import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  Container,
  Stepper,
  Step,
  StepLabel,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Support as SupportIcon,
} from '@mui/icons-material';

const Confirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;

  if (!order) {
    navigate('/client/dashboard');
    return null;
  }

  const totalAmount = Number(order.totalAmount || order.amount || 0);
  const earlyPaymentDiscount = totalAmount * 0.1;
  const discountedAmount = totalAmount - earlyPaymentDiscount;

  const handlePayNow = async () => {
    try {
      const { orderAPI } = await import('../../services/api');
      const response = await orderAPI.getPaymentLink(order.id);

      if (response.success && response.paymentLink) {
        window.open(response.paymentLink, '_blank', 'noopener,noreferrer');
      } else {
        navigate('/client/order-history');
      }
    } catch {
      navigate('/client/order-history');
    }
  };

  const handleTrackOrder = () => {
    navigate('/client/order-history');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb', p: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 1, textAlign: 'center' }}>
          <CardContent sx={{ py: 5 }}>
            <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Order Confirmed
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Thanks for your purchase! Your order is now in our fulfillment queue.
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                display: 'inline-block',
                bgcolor: '#ecfdf5',
                border: '1px solid #10b981',
                px: 2.5,
                py: 0.8,
                borderRadius: 1,
                fontWeight: 600,
                color: '#065f46'
              }}
            >
              #{order.orderNumber || order.id?.slice(-8).toUpperCase()}
            </Typography>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <Card sx={{ mb: 3, borderLeft: '4px solid #10b981' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Payment Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Net 60 terms applied. Invoice available in Order History.
            </Typography>

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Invoice Total</Typography>
                <Typography fontWeight={700}>${totalAmount.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="success.main">Early Payment (2 days)</Typography>
                <Typography fontWeight={700} color="success.main">
                  ${discountedAmount.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="success.main">You Save</Typography>
                <Typography fontWeight={700} color="success.main">
                  ${earlyPaymentDiscount.toFixed(2)}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ textAlign: 'right', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handlePayNow}
                sx={{
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                  fontWeight: 600,
                }}
              >
                Pay Now & Save 10%
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Progress Tracker */}
        <Card sx={{ mb: 3, textAlign: 'center', boxShadow: 0 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Order Progress
            </Typography>
            <Stepper activeStep={0} alternativeLabel>
              {['Processing', 'Preparing', 'Shipped', 'Delivered'].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, fontStyle: 'italic' }}
            >
              You’ll receive email updates as your order moves through each stage.
            </Typography>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={2}>
              Order Details
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Items Ordered
                </Typography>
                {order.items?.map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 0.5,
                    }}
                  >
                    <Typography>{item.product?.name || 'Product'}</Typography>
                    <Typography fontWeight={600}>×{item.quantity}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Shipping Method
                </Typography>
                <Typography>
                  {order.shippingMethod || 'Standard Delivery'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Temperature-controlled • Signature required
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* CTA */}
        <Box textAlign="center" mt={4}>
          <Button
            onClick={handleTrackOrder}
            variant="outlined"
            sx={{
              px: 4,
              py: 1.2,
              borderColor: '#10b981',
              color: '#065f46',
              fontWeight: 600,
              '&:hover': { bgcolor: '#ecfdf5' },
            }}
          >
            Track Order
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Confirmation;
